import { useAccount, useMsal } from "@azure/msal-react";
import {
    Button,
    Card,
    Container,
    Group,
    Select,
    Stack,
    Text,
    Textarea,
    Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCircleCheck, IconClock, IconExternalLink } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useMemo, useRef, useState } from "react";
import DatasourceRequestAccessModal from "src/components/DatasourceRequestAccessModal";
import { useOrganizations } from "src/hooks/useConfiguration";
import { fetchPendingRequests } from "src/hooks/useFetchPendingRequests";
import { type UserDatasourceDetail, useUser, useUserDatasources } from "src/hooks/useUser";
import { postAPI } from "src/utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

interface ProductLineOption {
    value: string;
    label: string;
}

interface DatasourceChipsProps {
    dataSources: UserDatasourceDetail[];
    selectedDataSources: Record<string, boolean>;
    onDataSourceToggle: (dataSourceId: string, selected: boolean) => void;
    requesting_access: boolean;
    pendingRequests: Set<string>;
    setPendingRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
}

function DatasourceChips({
    dataSources,
    selectedDataSources,
    onDataSourceToggle,
    requesting_access,
    pendingRequests,
    setPendingRequests,
}: DatasourceChipsProps) {
    const [accessRequestModalOpened, { open: openAccessRequest, close: closeAccessRequest }] =
        useDisclosure(false);
    const [selectedDatasource, setSelectedDatasource] = useState<UserDatasourceDetail | null>(null);

    if (dataSources.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No knowledge bases match the selected product line filter.
            </Text>
        );
    }

    return (
        <Stack gap={6}>
            <Group gap={8} align="center">
                {dataSources.map((dataSource) => {
                    const isSelected = selectedDataSources[dataSource.id] === true;
                    const hasAccess = requesting_access || dataSource.has_access !== false;

                    let iconElement = null;
                    let tooltipLabel = "";
                    let chipClickHandler: (() => void) | null = null;

                    if (hasAccess) {
                        iconElement = (
                            <IconCircleCheck
                                size="0.875rem"
                                color={isSelected ? "white" : "green"}
                            />
                        );
                        tooltipLabel = "Access granted";
                        chipClickHandler = () => {
                            onDataSourceToggle(dataSource.id, !isSelected);
                        };
                    } else if (pendingRequests.has(dataSource.id)) {
                        iconElement = <IconClock size="0.875rem" color="#f59e0b" />;
                        tooltipLabel = "Access request pending";
                    } else {
                        iconElement = <IconExternalLink size="0.875rem" color="blue" />;
                        tooltipLabel = "Request access";
                        chipClickHandler = () => {
                            setSelectedDatasource(dataSource);
                            openAccessRequest();
                        };
                    }

                    return (
                        <Tooltip key={dataSource.id} label={tooltipLabel}>
                            <Button
                                onClick={chipClickHandler ?? undefined}
                                size="xs"
                                disabled={!hasAccess && pendingRequests.has(dataSource.id)}
                                radius="sm"
                                variant="filled"
                                color={isSelected ? "violet" : "gray.2"}
                                styles={{
                                    root: {
                                        cursor: chipClickHandler ? "pointer" : "not-allowed",
                                        opacity: hasAccess ? 1 : 0.8,
                                        height: "24px",
                                        padding: "4px 8px",
                                    },
                                    inner: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontWeight: isSelected ? 600 : 400,
                                        fontSize: "12px",
                                        color: isSelected ? "white" : "#666",
                                    },
                                }}
                            >
                                {iconElement}
                                <span style={{ marginLeft: 6 }}>{dataSource.name}</span>
                            </Button>
                        </Tooltip>
                    );
                })}

                {selectedDatasource && (
                    <DatasourceRequestAccessModal
                        opened={accessRequestModalOpened}
                        onClose={() => {
                            closeAccessRequest();
                        }}
                        onSubmit={() => {
                            setPendingRequests((prev) => new Set([...prev, selectedDatasource.id]));
                        }}
                        title={`Request Access to ${selectedDatasource.name}`}
                        description={selectedDatasource.description}
                        ingestion_status={selectedDatasource.ingestion_status}
                        requested_datasources={[selectedDatasource.id]}
                    />
                )}
            </Group>

            {dataSources.length > 1 && (
                <Group gap={3} ml={4}>
                    <Text
                        fw={11}
                        c="dimmed"
                        style={{
                            cursor: "pointer",
                            fontWeight: 500,
                            opacity: 0.7,
                        }}
                        onClick={() => {
                            for (const ds of dataSources) {
                                const hasAccess = requesting_access || ds.has_access !== false;
                                if (hasAccess) {
                                    onDataSourceToggle(ds.id, true);
                                }
                            }
                        }}
                    >
                        All
                    </Text>
                    <Text fw={11} c="dimmed" style={{ opacity: 0.5 }}>
                        ·
                    </Text>
                    <Text
                        fw={11}
                        c="dimmed"
                        style={{
                            cursor: "pointer",
                            fontWeight: 500,
                            opacity: 0.7,
                        }}
                        onClick={() => {
                            for (const ds of dataSources) {
                                onDataSourceToggle(ds.id, false);
                            }
                        }}
                    >
                        None
                    </Text>
                </Group>
            )}
        </Stack>
    );
}

function Questionnaire({
    heading_text = "Would you like to filter the search results by product line and knowledge bases?",
    paragraph_text = `
    This is an optional filter to narrow down the search results, based on the product line and knowledge bases you are interested in.
    You can request access to the knowledge bases that you don't have access to just by clicking on the knowledge base.
    `,
    button_text = "Save Preferences",
    requesting_access = false,
}) {
    const TRAINING_URL =
        "https://siemens-energy-learning.sabacloud.com/Saba/Web_spf/EU5P1PRD001/app/me/learningeventdetail/cours000000000184778)";

    const [selectedProductLine, setSelectedProductLine] = useState<string>("all");
    const [dataSourceSelections, setDataSourceSelections] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [productLineDataSources, setProductLineDataSources] = useState<
        Record<string, Set<string>>
    >({});
    const [productLineOptions, setProductLineOptions] = useState<ProductLineOption[]>([]);
    const navigate = useNavigate();
    const { data: user } = useUser();
    const location = useLocation();
    const { source, requesting_access: requestingAccessParam } = location.search as {
        source?: string;
        requesting_access?: string;
    };
    const account = useAccount();
    const isExternalUser = user?.info?.roles?.includes("external") ?? false;

    const isRequestingAccess = requestingAccessParam === "true" || requesting_access;

    // External users skip datasource selection and go straight to chat
    useEffect(() => {
        if (isExternalUser && !isRequestingAccess) {
            navigate({ to: "/" });
        }
    }, [isExternalUser, isRequestingAccess, navigate]);

    // Compute display text based on isRequestingAccess mode
    const environment = import.meta.env.VITE_ENVIRONMENT;
    const displayHeadingText =
        isRequestingAccess && !requesting_access
            ? `Would you like to request access to Knowledge Search (${environment})?`
            : heading_text;
    const displayParagraphText =
        isRequestingAccess && !requesting_access
            ? "Please specify the knowledge bases that you need access to, an admin will review your request."
            : paragraph_text;
    const displayButtonText =
        isRequestingAccess && !requesting_access ? "Request Access" : button_text;

    const initialized = useRef(false);
    const productLineInitialized = useRef(false);

    const { data: userDatasourceDetails } = useUserDatasources();
    const { data: organizations } = useOrganizations();
    const [userDatasources, setUserDatasources] = useLocalStorage<UserDatasourceDetail[]>(
        "datasources",
        [],
    );

    const [chatPreferences, setChatPreferences] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    const [, setSelectedOrganization] = useLocalStorage<string>("selectedOrganization", "");

    const [, setSubmitting] = useState(false);
    const { instance } = useMsal();
    const queryClient = useQueryClient();

    const [requestReason, setRequestReason] = useState("");
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (initialized.current) return;
        if (!user || !userDatasourceDetails || !organizations) return;
        if (user.info.user_preferences.datasources.length > 0) {
            localStorage.setItem(
                "memoryEnabled",
                String(user.info.user_preferences.memory_enabled),
            );
            setChatPreferences({
                selectedProductLines: [user.info.user_preferences.product_line || "all"],
                selectedDatasources: user.info.user_preferences.datasources,
            });
        }
        initialized.current = true;
        setUserDatasources(userDatasourceDetails);

        const productLineToDataSources: Record<string, Set<string>> = {};
        productLineToDataSources.all = new Set(userDatasourceDetails.map((ds) => ds.id));

        for (const org of organizations) {
            for (const productLine of org.product_lines) {
                productLineToDataSources[productLine] = new Set(
                    userDatasourceDetails
                        .filter((ds) => ds.product_line_filter.includes(productLine))
                        .map((ds) => ds.id),
                );
            }
        }

        setProductLineDataSources(productLineToDataSources);

        const orgOrder = [
            "GS D AGT",
            "GS D MGT",
            "GS D SGT",
            "GS C",
            "TI CP (RCE & TCP)",
            "TI STG",
        ];

        const options: ProductLineOption[] = [];
        for (const orgId of orgOrder) {
            const org = organizations.find((o) => o.id === orgId);
            if (org && org.product_lines.length > 0) {
                const productLine = org.product_lines[org.product_lines.length - 1];
                options.push({
                    value: productLine,
                    label: org.description || productLine,
                });
            }
        }

        setProductLineOptions(options);
    }, [user, userDatasourceDetails, organizations, setUserDatasources, setChatPreferences]);

    useEffect(() => {
        if (productLineInitialized.current) return;
        if (!chatPreferences || Object.keys(productLineDataSources).length === 0) return;

        productLineInitialized.current = true;

        const initialProductLine =
            chatPreferences.selectedProductLines?.length > 0
                ? chatPreferences.selectedProductLines[0]
                : "all";

        setSelectedProductLine(initialProductLine);

        const initialSelections: Record<string, boolean> = {};
        const availableIds = productLineDataSources[initialProductLine];

        if (availableIds) {
            if (chatPreferences.selectedDatasources?.length > 0) {
                for (const dsId of chatPreferences.selectedDatasources) {
                    const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                    if (availableIds.has(dsId) && datasource?.has_access) {
                        initialSelections[dsId] = true;
                    }
                }

                if (Object.keys(initialSelections).length === 0) {
                    for (const dsId of availableIds) {
                        const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                        initialSelections[dsId] = datasource?.has_access ?? false;
                    }
                }
            } else {
                for (const dsId of availableIds) {
                    const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                    initialSelections[dsId] = datasource?.has_access ?? false;
                }
            }
        }
        setDataSourceSelections(initialSelections);
    }, [chatPreferences, productLineDataSources, user]);

    const filteredDataSources = useMemo(() => {
        if (!selectedProductLine || selectedProductLine === "all") {
            return userDatasources;
        }
        return userDatasources.filter((ds) =>
            (ds.product_line_filter ?? []).includes(selectedProductLine),
        );
    }, [userDatasources, selectedProductLine]);

    const handleProductLineFilterChange = (value: string | null) => {
        const nextProductLine = value || "all";
        setSelectedProductLine(nextProductLine);

        const availableIds =
            productLineDataSources[nextProductLine] ?? new Set(userDatasources.map((ds) => ds.id));

        const freshSelections: Record<string, boolean> = {};
        for (const dsId of availableIds) {
            const datasource = user?.datasources?.find((ds) => ds.id === dsId);
            freshSelections[dsId] = (isRequestingAccess || datasource?.has_access) ?? false;
        }
        setDataSourceSelections(freshSelections);
    };

    const handleDataSourceToggle = (dataSourceId: string, isChecked: boolean) => {
        setDataSourceSelections((prev) => ({
            ...prev,
            [dataSourceId]: isChecked,
        }));
    };

    const handleRequestAccess = async () => {
        try {
            if (!account || !requestReason.trim()) return;
            setSubmitting(true);
            setLoading(true);
            const selectedDataSources = Object.keys(dataSourceSelections).filter(
                (key) => dataSourceSelections[key] === true,
            );
            const response = await postAPI({
                url: `${BACKEND_URL}/v1/user/access-requests`,
                payload: {
                    user_id:
                        account.idTokenClaims?.gid ||
                        account.idTokenClaims?.SCGID ||
                        account.idTokenClaims?.siemens_gid ||
                        account.localAccountId,
                    user_email: account.username,
                    requested_datasources: selectedDataSources,
                    reason: requestReason,
                },
                instance,
            });
            if (response.status >= 300) {
                throw new Error(
                    "Failed to request access, Please try again later or contact our product owner",
                );
            }
            notifications.show({
                title: "Saved",
                message:
                    "We'd review your request as soon as possible and get back to you, thanks for requesting access.",
            });

            setTimeout(() => {
                const newWindow = window.open(TRAINING_URL, "_blank", "noopener,noreferrer");

                if (newWindow) newWindow.opener = null;
                window.location.reload();
            }, 2000);
        } catch (error: unknown) {
            const err = error as {
                message?: string;
                response?: { data?: { error?: { message?: string } } };
            };
            notifications.show({
                title: "Error",
                color: "red",
                message:
                    err.message ??
                    err.response?.data?.error?.message ??
                    "Error submitting request.",
            });
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    const handleSavePreferences = async () => {
        const selectedDataSources = Object.keys(dataSourceSelections).filter(
            (key) => dataSourceSelections[key] === true,
        );

        let selectedOrgName = "";
        if (selectedProductLine && selectedProductLine !== "all") {
            for (const org of organizations || []) {
                if (org.product_lines.includes(selectedProductLine)) {
                    selectedOrgName = org.id;
                    break;
                }
            }
        }

        const storedMemoryPreference = localStorage.getItem("memoryEnabled");
        const memoryPreference = storedMemoryPreference ? storedMemoryPreference === "true" : false;

        setLoading(true);

        try {
            await postAPI({
                url: `${BACKEND_URL}/v1/user/preferences`,
                payload: {
                    product_line: selectedProductLine.toUpperCase(),
                    datasources: selectedDataSources,
                    memory_enabled: memoryPreference,
                },
                instance,
                expectedStatus: 200,
            });

            setChatPreferences({
                selectedProductLines: selectedProductLine ? [selectedProductLine] : [],
                selectedDatasources: selectedDataSources,
            });

            setSelectedOrganization(selectedOrgName);

            if (window.opener) {
                window.opener.postMessage({ type: "cts-preferences-updated" }, "*");
            }

            setTimeout(async () => {
                await queryClient.invalidateQueries({ queryKey: ["user"] });
                if (source) {
                    await navigate({ to: source });
                } else {
                    await navigate({ to: "/" });
                }
                setLoading(false);
            }, 100);
        } catch (error) {
            console.error("Error updating user preferences: ", error);
            notifications.show({
                title: "Error",
                color: "red",
                message: "Failed to save preferences. Please try again.",
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                setPendingRequests(await fetchPendingRequests(instance));
            } catch (err) {
                console.error("Failed to fetch pending requests:", err);
            }
        };
        fetchRequests();
    }, [instance]);

    const visibleSelections = useMemo(() => {
        const visibleIds = new Set(filteredDataSources.map((ds) => ds.id));
        const result: Record<string, boolean> = {};
        for (const [id, selected] of Object.entries(dataSourceSelections)) {
            if (visibleIds.has(id)) {
                result[id] = selected;
            }
        }
        return result;
    }, [dataSourceSelections, filteredDataSources]);

    return (
        <main className="mx-auto p-4">
            <Container size="md">
                <div>
                    <Text size="xl" fw={600}>
                        {displayHeadingText}
                    </Text>
                    <Text size="sm" c="dimmed" mt={24} mb={24} style={{ whiteSpace: "pre-line" }}>
                        {displayParagraphText}
                    </Text>

                    <Stack gap="md">
                        <Group align="center" gap="xs" wrap="nowrap" w="100%">
                            <Text size="sm" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                                Filter by Product Line:
                            </Text>
                            <Select
                                aria-label="Filter by Product Line"
                                placeholder="No selection"
                                data={[
                                    { value: "all", label: "No selection" },
                                    ...productLineOptions,
                                ]}
                                value={selectedProductLine}
                                onChange={handleProductLineFilterChange}
                                searchable
                                clearable
                                nothingFoundMessage="No product line found"
                                clearButtonProps={{ "aria-label": "Clear product line filter" }}
                                style={{ flex: 1 }}
                            />
                        </Group>

                        <Card shadow="sm" p="lg" radius="md" withBorder w="100%">
                            <Text size="sm" fw={600} mb="sm">
                                Knowledge Bases
                            </Text>
                            <DatasourceChips
                                dataSources={filteredDataSources}
                                selectedDataSources={visibleSelections}
                                onDataSourceToggle={handleDataSourceToggle}
                                requesting_access={isRequestingAccess}
                                pendingRequests={pendingRequests}
                                setPendingRequests={setPendingRequests}
                            />
                        </Card>

                        <Textarea
                            required
                            label="Reason for access:"
                            placeholder="Example: I need it to help me with my daily customer support work..."
                            style={{ flex: 1 }}
                            size="xl"
                            value={requestReason}
                            onChange={(event) => setRequestReason(event.currentTarget.value)}
                            styles={{
                                root: { display: isRequestingAccess ? "block" : "none" },
                                label: { fontSize: "16px" },
                                input: { fontSize: "16px" },
                            }}
                        />
                    </Stack>

                    <Group justify="flex-end" mt={24}>
                        <Button
                            variant="default"
                            disabled={loading}
                            onClick={() => navigate({ to: source ?? "/" })}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={
                                isRequestingAccess ? handleRequestAccess : handleSavePreferences
                            }
                            disabled={
                                loading ||
                                !selectedProductLine ||
                                (isRequestingAccess && !requestReason.trim()) ||
                                Object.values(dataSourceSelections).every((selected) => !selected)
                            }
                            loading={loading}
                            style={(theme) => ({
                                "&:disabled": {
                                    backgroundColor: theme.colors.gray[2],
                                    color: theme.colors.gray[5],
                                },
                            })}
                        >
                            {loading ? "Submitting..." : displayButtonText}
                        </Button>
                    </Group>
                </div>
            </Container>
        </main>
    );
}

export const Route = createFileRoute("/_layout-nosidebar/questionnaire")({
    component: Questionnaire,
});
export default Questionnaire;
